import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PagePublic from './PagePublic';
import CustomPageLoading from './CustomPageLoading';
import EmptyPage from './EmptyPage';
import { isCustomDomain, resolvePageForDomain } from '../utils/domainHelpers';

/**
 * Custom Domain Router Component
 * Handles routing for custom domains by resolving domain + path to pages
 */
const CustomDomainRouter = () => {
  const [pageFound, setPageFound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const resolveCustomDomain = async () => {
      try {
        // 🔥 FIX: Check if this is a proxy request from Cloudflare Worker
        const originalHost = window.__BC_ORIGINAL_HOST__ || 
                            new URLSearchParams(window.location.search).get('__bc_original_host');
        
        console.log('🔍 CustomDomainRouter: Proxy detection debug', {
          originalHost,
          windowOriginalHost: window.__BC_ORIGINAL_HOST__,
          urlParam: new URLSearchParams(window.location.search).get('__bc_original_host'),
          currentPath: location.pathname,
          currentHostname: window.location.hostname,
          isToRoute: location.pathname.startsWith('/to/')
        });
        
        // 🔥 FIX: If we have originalHost, this is a proxy request - render PagePublic directly
        if (originalHost) {
          console.log('🔄 CustomDomainRouter: Proxy request detected (via originalHost), rendering PagePublic directly', {
            originalHost,
            currentPath: location.pathname,
            currentHostname: window.location.hostname,
            isToRoute: location.pathname.startsWith('/to/')
          });
          
          // Set a dummy page found to trigger PagePublic rendering
          setPageFound({ 
            type: 'proxy-direct',
            originalHost,
            path: location.pathname
          });
          setLoading(false);
          return;
        }
        
        // Standard custom domain resolution - but this path should not be reached for proxy requests
        const domain = originalHost || window.location.hostname;
        const path = location.pathname;

        console.log('🌐 CustomDomainRouter: Standard domain resolution (non-proxy)', { 
          domain, 
          path, 
          originalHost,
          currentHostname: window.location.hostname,
          isProxied: !!originalHost
        });

        // For now, show EmptyPage for non-proxy custom domain requests
        // The domain mapping system would need to be configured for direct custom domain access
        console.log('⚠️ CustomDomainRouter: Direct custom domain access not configured, showing EmptyPage');
        setPageFound(null);

      } catch (error) {
        console.error('❌ CustomDomainRouter: Error resolving domain', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    resolveCustomDomain();
  }, [location.pathname]);

  if (loading) {
    // Extract spaceId from URL path if available (no hardcoded mappings!)
    const originalHost = window.__BC_ORIGINAL_HOST__ || 
                        new URLSearchParams(window.location.search).get('__bc_original_host');
    
    let customDomainSpaceId = null;
    
    // Try to extract from /to/ route
    if (location.pathname.includes('/to/')) {
      const pathMatch = location.pathname.match(/\/to\/([^\/\?]+)/);
      if (pathMatch) {
        customDomainSpaceId = pathMatch[1];
      }
    }
    
    return <CustomPageLoading 
      source="custom-domain-router" 
      customDomainSpaceId={customDomainSpaceId}
    />;
  }

  if (error) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center',
        color: '#666',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        <h2>🌐 Custom Domain Error</h2>
        <p>There was an issue resolving this custom domain.</p>
        <p><small>{error}</small></p>
      </div>
    );
  }

  if (!pageFound) {
    // No page found for this custom domain
    return <EmptyPage />;
  }

  // Render PagePublic with resolved page data
  // PagePublic will handle the actual page rendering
  return <PagePublic />;
};

export default CustomDomainRouter;
